import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticle, getRelatedArticles } from '../api';

function BlogDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadArticle = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [articleRes, relatedRes] = await Promise.all([
                getArticle(slug),
                getRelatedArticles(slug)
            ]);
            setArticle(articleRes.data);
            setRelatedArticles(relatedRes.data);
            
            // Update page title
            document.title = articleRes.data.meta_title || articleRes.data.title + ' - Evomotor Blog';
        } catch (err) {
            console.error('Error loading article:', err);
            setError('Articolo non trovato');
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        loadArticle();
    }, [loadArticle]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getTags = () => {
        if (!article?.tags) return [];
        return article.tags.split(',').map(t => t.trim()).filter(t => t);
    };

    if (loading) {
        return <div className="loading">Caricamento...</div>;
    }

    if (error || !article) {
        return (
            <div className="blog-container">
                <div className="no-articles">
                    <p>{error || 'Articolo non trovato'}</p>
                    <Link to="/blog" className="back-link">← Torna al Blog</Link>
                </div>
            </div>
        );
    }

    const canonicalUrl = window.location.href;
    const tags = getTags();

    return (
        <>
            {/* SEO Meta Tags */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BlogPosting",
                    "headline": article.title,
                    "description": article.meta_description || article.title,
                    "url": canonicalUrl,
                    "image": article.cover_image_url ? `${article.cover_image_url}` : undefined,
                    "datePublished": article.published_at,
                    "dateModified": article.updated_at,
                    "author": {
                        "@type": "Organization",
                        "name": "Evomotor"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Evomotor",
                        "url": "https://evomotor.it"
                    }
                })}
            </script>

            <article className="article-detail">
                <div className="article-container">
                    <header className="article-header">
                        <nav className="breadcrumb">
                            <Link to="/blog">Blog</Link>
                            <span className="separator">/</span>
                            {article.category && (
                                <>
                                    <Link to={`/blog?category=${encodeURIComponent(article.category)}`}>
                                        {article.category}
                                    </Link>
                                    <span className="separator">/</span>
                                </>
                            )}
                            <span className="current">{article.title}</span>
                        </nav>

                        <h1 className="article-main-title">{article.title}</h1>

                        <div className="article-meta">
                            {article.published_at && (
                                <time dateTime={article.published_at}>
                                    {formatDate(article.published_at)}
                                </time>
                            )}
                            {article.category && (
                                <>
                                    <span className="meta-separator">•</span>
                                    <Link 
                                        to={`/blog?category=${encodeURIComponent(article.category)}`}
                                        className="category-link"
                                    >
                                        {article.category}
                                    </Link>
                                </>
                            )}
                        </div>

                        {tags.length > 0 && (
                            <div className="article-tags">
                                {tags.map(tag => (
                                    <span key={tag} className="tag">{tag}</span>
                                ))}
                            </div>
                        )}
                    </header>

                    {article.cover_image_url && (
                        <figure className="article-cover">
                            <img
                                src={`${article.cover_image_url}`}
                                alt={article.title}
                                className="cover-image"
                            />
                        </figure>
                    )}

                    <div 
                        className="article-body"
                        dangerouslySetInnerHTML={{ __html: article.content_html }}
                    />

                    {relatedArticles.length > 0 && (
                        <aside className="related-articles">
                            <h2>Articoli correlati</h2>
                            <div className="related-grid">
                                {relatedArticles.map(related => (
                                    <Link 
                                        key={related.id} 
                                        to={`/blog/${related.slug}`}
                                        className="related-card"
                                    >
                                        {related.cover_image_url ? (
                                            <img
                                                src={`${related.cover_image_url}`}
                                                alt={related.title}
                                                className="related-image"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="related-image" style={{ 
                                                background: '#f0f0f0', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center' 
                                            }}>
                                                Evomotor
                                            </div>
                                        )}
                                        <span className="related-title">{related.title}</span>
                                    </Link>
                                ))}
                            </div>
                        </aside>
                    )}

                    <nav className="article-nav">
                        <Link to="/blog" className="back-link">← Torna al Blog</Link>
                    </nav>
                </div>
            </article>
        </>
    );
}

export default BlogDetail;
